import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { trackException } from '@/lib/telemetry';

// Catches render errors anywhere below it. Shows a recover-able UI instead of
// a white screen, and ships the exception to App Insights so we can debug.

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    trackException(error, { componentStack: info.componentStack ?? undefined });
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle={this.state.error.message || 'An unexpected error occurred.'}
          extra={[
            <Button key="reload" type="primary" icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
              Reload
            </Button>,
            <Button key="dismiss" onClick={this.reset}>
              Dismiss
            </Button>,
          ]}
          style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        />
      );
    }
    return this.props.children;
  }
}
